import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { NewCadrageModal } from '../../components/Cadrages/NewCadrageModal';

import { AppWindow, ArrowLeft, BarChart3, CheckCircle, ClipboardList, FileText, Files, Folder, MessageCircle } from 'lucide-react';
import { AskAgentButton } from '../../components/Common/AskAgentButton';
import { Tab } from '@headlessui/react';
import { useUseCaseData } from '../../hooks/useUseCaseData';
import { useUseCaseEditor } from '../../hooks/useUseCaseEditor';
import { useExcalidrawPersistence } from '../../hooks/useExcalidrawPersistence';
import { useUseCaseQualification } from '../../hooks/useUseCaseQualification';
import { useUseCaseDiscussion } from '../../hooks/useUseCaseDiscussion';
import { useUseCasePersonas } from '../../hooks/useUseCasePersonas';
import UseCaseQualificationTab from '../../components/UseCases/Qualification/UseCaseQualificationTab';
import DiscussionTab from '../../components/UseCases/Discussion/DiscussionTab';
import { PageLayout } from '../../components/Layout/PageLayout';
import { NotFoundState } from '../../components/Common/NotFoundState';
import UseCaseOverviewTab from '../../components/UseCases/Overview/UseCaseOverviewTab';
import UseCaseBusinessDescriptionTab from '../../components/UseCases/BusinessDescription/UseCaseBusinessDescriptionTab';
import DocumentManagerSection from '../../components/Documents/DocumentManagerSection';
import { CoWorkHandoffButton } from '../../components/UseCases/CoWorkHandoffButton';
import { getUseCaseStatusLabel } from '../../components/UseCases/useCaseStatus';
import { domainService } from '../../services/domainService';
import cadrageService from '../../services/cadrageService';
import { Badge } from '@/components/Common/badge';
import { Button } from '@/components/Common/button';
import { ConfirmationModal } from '../../components/Common/ConfirmationModal';
import useCaseService from '../../services/useCaseService';
import type { CadrageReadWithTeam, UseCaseStatus } from '../../types';
import { useTranslation } from 'react-i18next';
import { ApplicationManagementModule, ApplicationSelector } from '../../components/Forge';
import forgeService from '../../services/forgeService';
import { useForgeApiEnabled } from '../../stores/forgeApiStore';
import { useAuthStore, useIsOrganizationAdmin } from '../../stores/authStore';

const UseCaseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useAuthStore((state) => state.user);
  const isAdmin = useIsOrganizationAdmin();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const { useCase, isLoading: isLoadingUseCase, error: errorUseCase, loadError: loadErrorUseCase, refetchUseCase } = useUseCaseData();

  const [linkedCadrages, setLinkedCadrages] = useState<CadrageReadWithTeam[]>([]);
  const [isLoadingCadrages, setIsLoadingCadrages] = useState(false);
  const [cadragesError, setCadragesError] = useState<string | null>(null);
  const [isNewCadrageModalOpen, setIsNewCadrageModalOpen] = useState(false);

  const fetchLinkedCadrages = useCallback(async () => {
    if (!id) return;
    setIsLoadingCadrages(true);
    setCadragesError(null);
    try {
      const all = await cadrageService.getCadrages();
      const filtered = all.filter((c) => c.use_case_id === id);
      setLinkedCadrages(filtered);
    } catch (e: any) {
      setCadragesError(e?.message || t('useCaseDetail.cadrages.errorLoading'));
    } finally {
      setIsLoadingCadrages(false);
    }
  }, [id, t]);

  useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      await fetchLinkedCadrages();
      if (ignore) return;
    };
    fetchData();
    return () => {
      ignore = true;
    };
  }, [fetchLinkedCadrages]);

  const {
    editedShortDescription,
    editedDescription,
    editedRecommendation,
    editedStatus,
    edited_value_score,
    edited_complexity_score,
    editedBusinessNeed,
    editedInput,
    editedOutput,
    editedInvolvedTools,
    editedTechElements,
    customToolInput,
    setEditedShortDescription,
    setEditedDescription,
    setEditedRecommendation,
    setEditedStatus,
    set_edited_value_score,
    set_edited_complexity_score,
    setEditedBusinessNeed,
    setEditedInput,
    setEditedOutput,
    setEditedInvolvedTools,
    setEditedTechElements,
    setCustomToolInput,
    handleSaveChanges,
  } = useUseCaseEditor(useCase);

  const {
    data: excalidrawData,
    setData: setExcalidrawData,
    isLoading: isLoadingFlow,
    error: errorFlow,
    saveDiagram: saveFlowDiagram,
  } = useExcalidrawPersistence({ type: 'use-case', id: id });

  const qualificationHookResult = useUseCaseQualification(id, {
    onQualificationCreated: refetchUseCase,
  });
  const discussionHookResult = useUseCaseDiscussion(id);
  const personasHookResult = useUseCasePersonas(id);

  const [domainName, setDomainName] = useState<string | null>(null);
  const [domainForgeId, setDomainForgeId] = useState<string | null>(null);
  const [hasDomainForgeClient, setHasDomainForgeClient] = useState(true);
  useEffect(() => {
    let ignore = false;
    const load = async () => {
      try {
        const wsId = (useCase as any)?.domain_id as string | undefined;
        const wsName = (useCase as any)?.domain_name as string | undefined;
        if (wsName) {
          if (!ignore) setDomainName(wsName);
        }
        if (wsId) {
          const details = await domainService.getDomainDetails(wsId);
          if (!ignore) {
            setDomainName(details.name);
            setDomainForgeId((details as any).forge_id ?? null);
            setHasDomainForgeClient(Boolean((details as any).organization?.forge_client_id));
          }
        } else {
          if (!ignore) {
            setDomainName(null);
            setHasDomainForgeClient(false);
          }
        }
      } catch {
        if (!ignore) {
          setDomainName(null);
          setHasDomainForgeClient(false);
        }
      }
    };
    load();
    return () => { ignore = true; };
  }, [useCase]);

  // The Application tab also disappears while the super-admin Forge API switch is off.
  const forgeApiEnabled = useForgeApiEnabled() === true;
  const showApplicationTab = hasDomainForgeClient && forgeApiEnabled;

  // Base tabs (6) plus the optional Application tab. Reset to the first tab if
  // the selected index falls out of range when the Application tab is hidden.
  const visibleTabCount = 6 + (showApplicationTab ? 1 : 0);
  useEffect(() => {
    if (selectedTabIndex >= visibleTabCount) {
      setSelectedTabIndex(0);
    }
  }, [visibleTabCount, selectedTabIndex]);

  // Application error detection
  const [appLoadError, setAppLoadError] = useState<string | null>(null);
  const [showChangeApp, setShowChangeApp] = useState(false);
  const [pendingAppId, setPendingAppId] = useState<string | null>(null);
  const [isSavingApp, setIsSavingApp] = useState(false);
  const [isUnlinkingApp, setIsUnlinkingApp] = useState(false);

  // Delete states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [useCaseNameInput, setUseCaseNameInput] = useState('');

  useEffect(() => {
    const applicationId = useCase?.forge_application_id;
    if (!applicationId || !forgeApiEnabled) { setAppLoadError(null); return; }
    let ignore = false;
    forgeService.getApplication(applicationId)
      .then(() => { if (!ignore) setAppLoadError(null); })
      .catch((err: any) => {
        console.error('Error loading application:', err);
        if (!ignore) {
          // Si l'application n'existe plus (404), afficher un message spécifique
          if (err.response?.status === 404) {
            setAppLoadError(t('useCase.forge.applicationNotFound', 'Application Forge non trouvée (peut-être supprimée)'));
          } else {
            setAppLoadError(t('global.somethingWentWrong', 'Something went wrong'));
          }
        }
      });
    return () => { ignore = true; };
  }, [useCase?.forge_application_id, forgeApiEnabled, t]);

  const handleSaveAppChange = async () => {
    if (!id) return;
    try {
      setIsSavingApp(true);
      await forgeService.mapUseCaseToApplication(id, pendingAppId);
      await refetchUseCase();
      setAppLoadError(null);
      setShowChangeApp(false);
      setPendingAppId(null);
    } catch (err: any) {
      console.error('Failed to update application mapping', err);
    } finally {
      setIsSavingApp(false);
    }
  };

  const handleUnlinkApp = async () => {
    if (!id) return;
    try {
      setIsUnlinkingApp(true);
      await forgeService.mapUseCaseToApplication(id, null);
      await refetchUseCase();
      setAppLoadError(null);
      setShowChangeApp(false);
      setPendingAppId(null);
    } catch (err: any) {
      console.error('Failed to unlink application', err);
    } finally {
      setIsUnlinkingApp(false);
    }
  };

  const handleDeleteUseCase = () => {
    setUseCaseNameInput('');
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!id || !useCase) return;

    try {
      setIsDeleting(true);

      await useCaseService.remove(id);

      // Navigate back to use cases list with success message
      navigate('/cas-usage/list', {
        state: { message: t('useCaseDetail.deleteSuccess', { name: useCase.name }) }
      });
    } catch (err: any) {
      console.error('Failed to delete use case', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setUseCaseNameInput('');
  };

  const canCreateCadrage = useMemo(() => {
    if (!useCase) return false;
    const order: Record<UseCaseStatus, number> = {
      Identified: 0,
      Qualified: 1,
      Prioritized: 2,
      'In Progress': 3,
      Completed: 4,
      Abandoned: -1,
    };
    const current = order[useCase.status as UseCaseStatus] ?? 0;
    return current >= order.Qualified;
  }, [useCase]);

  const handleSaveAndRefetch = useCallback(async () => {
    try {
      const success = await handleSaveChanges();
      if (success) await refetchUseCase();
    } catch (error: any) {
      console.error(t('useCaseDetail.saveError'), error);
    }
  }, [handleSaveChanges, refetchUseCase, t]);


  if (isLoadingUseCase) {
    return (
      <PageLayout icon={<ClipboardList className="h-6 w-6" />} title={t('useCases.title')} subtitle={t('useCaseDetail.loading')}>
        <div className="text-center text-muted-foreground">{t('useCaseDetail.loading')}</div>
      </PageLayout>
    );
  }

  if (errorUseCase) {
    return (
      <PageLayout icon={<ClipboardList className="h-6 w-6" />} title={t('useCases.title')} subtitle={t('useCaseDetail.errorPrefix')}>
        <NotFoundState error={loadErrorUseCase}>
          <div className="text-center text-red-600">{t('useCaseDetail.errorPrefix')} {errorUseCase}</div>
        </NotFoundState>
      </PageLayout>
    );
  }

  if (!useCase) {
    return (
      <PageLayout icon={<ClipboardList className="h-6 w-6" />} title={t('useCases.title')} subtitle={t('useCaseDetail.notFound')}>
        <div className="text-center text-red-600">{t('useCaseDetail.notFound')}</div>
      </PageLayout>
    );
  }

  const statusLabel = getUseCaseStatusLabel(useCase.status, t);

  return (
    <PageLayout
      icon={<ClipboardList className="h-6 w-6" />}
      title={useCase.name}
      subtitle={domainName ? t('useCaseDetail.inDomain', 'In {{domain}}', { domain: domainName }) + ' · ' + statusLabel : statusLabel}
      titleSuffix={
        id ? (
          <AskAgentButton entityType="usecase" entityId={id} entityName={useCase.name} />
        ) : null
      }
      headerRight={
        <div className="flex items-center gap-2">
          {id ? <CoWorkHandoffButton useCaseId={id} useCaseName={useCase.name} /> : null}
          <Button
            onClick={() => navigate('/cas-usage/list')}
            variant="outline"
            size="sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('useCaseDetail.backToList', 'Retour à la liste')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <Tab.Group selectedIndex={selectedTabIndex} onChange={setSelectedTabIndex}>
          <Tab.List className="flex space-x-2 rounded-xl bg-muted p-1 mb-4">
            {[
              { label: t('global.overview'), icon: BarChart3 },
              { label: t('useCaseDetail.tabs.businessDescription'), icon: FileText },
              { label: t('useCaseDetail.tabs.qualification'), icon: CheckCircle },
              { label: t('useCaseDetail.tabs.cadrage'), icon: Folder },
              { label: t('useCaseDetail.tabs.relatedDocuments'), icon: Files },
              showApplicationTab ? { label: t('global.application'), icon: AppWindow } : null,
              { label: t('useCaseDetail.tabs.discussion'), icon: MessageCircle },
            ].filter(Boolean).map((tab) => {
              const { label, icon: Icon } = tab as { label: string; icon: React.ComponentType<{ className?: string }> };
              return (
                <Tab
                  key={label}
                  className={({ selected }) =>
                    `w-full rounded-md px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center space-x-2
                    ${selected ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'}`
                  }
                >
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                </Tab>
              );
            })}
          </Tab.List>

          <Tab.Panels className="mt-4">
            <Tab.Panel>
              <UseCaseOverviewTab
                useCase={useCase}
                editedShortDescription={editedShortDescription}
                editedDescription={editedDescription}
                editedRecommendation={editedRecommendation}
                editedStatus={editedStatus}
                edited_value_score={edited_value_score}
                edited_complexity_score={edited_complexity_score}
                onShortDescriptionChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditedShortDescription(e.target.value)}
                onDescriptionChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditedDescription(e.target.value)}
                onRecommendationChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditedRecommendation(e.target.value)}
                onStatusChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEditedStatus(e.target.value)}
                on_value_score_change={set_edited_value_score}
                on_complexity_score_change={set_edited_complexity_score}
                linkedCadrages={linkedCadrages}
                onSave={handleSaveAndRefetch}
                onDelete={handleDeleteUseCase}
                isDeleting={isDeleting}
              />
            </Tab.Panel>
            <Tab.Panel>
              <UseCaseBusinessDescriptionTab
                useCase={useCase}
                editedBusinessNeed={editedBusinessNeed}
                editedInput={editedInput}
                editedOutput={editedOutput}
                editedInvolvedTools={editedInvolvedTools}
                customToolInput={customToolInput}
                editedTechElements={editedTechElements}
                excalidrawData={excalidrawData}
                setExcalidrawData={setExcalidrawData}
                isLoadingFlow={isLoadingFlow}
                errorFlow={errorFlow}
                associatedPersonas={personasHookResult.associatedPersonas}
                isLoadingPersonas={personasHookResult.isLoading}
                errorPersonas={personasHookResult.error}
                onBusinessNeedChange={(value) => setEditedBusinessNeed(value)}
                onInputChange={(value) => setEditedInput(value)}
                onOutputChange={(value) => setEditedOutput(value)}
                onInvolvedToolsChange={setEditedInvolvedTools}
                onCustomToolInputChange={(e) => setCustomToolInput(e.target.value)}
                onAddCustomTool={() => {
                  const trimmedTool = customToolInput.trim();
                  if (trimmedTool && !editedInvolvedTools.includes(trimmedTool)) {
                    setEditedInvolvedTools(prev => [...prev, trimmedTool]);
                    setCustomToolInput('');
                  }
                }}
                onTechElementsChange={(value) => setEditedTechElements(value)}
                onSaveFlowDiagram={saveFlowDiagram}
                updateAssociatedPersonas={personasHookResult.updateAssociatedPersonas}
                fetchAssociatedPersonas={personasHookResult.fetchAssociatedPersonas}
                onSave={handleSaveAndRefetch}
              />
            </Tab.Panel>
            <Tab.Panel>
              <UseCaseQualificationTab
                useCaseId={id!}
                latestQualification={qualificationHookResult.latestQualification}
                qualificationHistory={qualificationHookResult.qualificationHistory}
                createQualification={qualificationHookResult.createQualification}
                updateQualification={qualificationHookResult.updateQualification}
                deleteQualification={qualificationHookResult.deleteQualification}
                isLoading={qualificationHookResult.isLoading}
                error={qualificationHookResult.error}
                editingQualification={qualificationHookResult.editingQualification}
                setEditingQualification={qualificationHookResult.setEditingQualification}
                currentUserId={currentUser?.id}
                isAdmin={isAdmin}
              />
            </Tab.Panel>
            <Tab.Panel>
              <div className="bg-card border border-border rounded-xl p-6 space-y-6">
                <h2 className="text-xl font-semibold text-foreground border-b border-border pb-2">
                  {t('useCaseDetail.cadrages.title')}
                </h2>
                <p className="text-sm text-muted-foreground -mt-4">
                  {t('useCaseDetail.cadrages.subtitle')}
                </p>

                {isLoadingCadrages && (
                  <p className="text-sm text-muted-foreground">
                    {t('useCaseDetail.cadrages.loading')}
                  </p>
                )}

                {cadragesError && (
                  <p className="text-sm text-destructive">{cadragesError}</p>
                )}

                {!isLoadingCadrages && !cadragesError && linkedCadrages.length === 0 && (
                  <div className="text-center py-8 space-y-4">
                    <p className="text-sm text-muted-foreground">
                      {t('useCaseDetail.cadrages.none')}
                    </p>
                    {canCreateCadrage ? (
                      <Button
                        size="default"
                        onClick={() => setIsNewCadrageModalOpen(true)}
                      >
                        {t('useCaseDetail.cadrages.create')}
                      </Button>
                    ) : (
                      <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 max-w-md mx-auto">
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                          {t('useCaseDetail.cadrages.statusWarning')}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {!isLoadingCadrages && !cadragesError && linkedCadrages.length > 0 && (() => {
                  const cadrage = linkedCadrages[0];
                  return (
                    <div className="bg-card border border-border rounded-lg">
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                              <h3 className="text-base font-semibold text-foreground">
                                {cadrage.name}
                              </h3>
                              {cadrage.type && (
                                <Badge variant="secondary">{cadrage.type}</Badge>
                              )}
                            </div>
                          </div>
                          <Link
                            to={`/cadrages/${cadrage.id}`}
                            className="text-xs font-medium text-primary hover:underline"
                          >
                            {t('useCaseDetail.cadrages.seeCadrage')}
                          </Link>
                        </div>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                          <div>
                            <span className="font-medium text-foreground">
                              {t('useCaseDetail.cadrages.client')}{' '}
                            </span>
                            {cadrage.company_name || t('global.notAvailable')}
                          </div>
                          <div>
                            <span className="font-medium text-foreground">
                              {t('useCaseDetail.cadrages.createdAt')}{' '}
                            </span>
                            {cadrage.created_at
                              ? new Date(cadrage.created_at).toLocaleDateString(navigator.language || 'en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })
                              : t('global.notAvailable')}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </Tab.Panel>
            <Tab.Panel>
              <div className="bg-card border border-border rounded-xl p-6 space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-foreground border-b border-border pb-2">
                    {t('useCaseDetail.relatedDocuments.title')}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('useCaseDetail.relatedDocuments.subtitle')}
                  </p>
                </div>
                <DocumentManagerSection context={id ? { type: 'use-case', id } : undefined} />
              </div>
            </Tab.Panel>
            {showApplicationTab && (
              <Tab.Panel>
                <div className="bg-card border border-border rounded-xl p-6 space-y-6">
                  <h2 className="text-xl font-semibold text-foreground border-b border-border pb-2">
                    {t('useCaseDetail.application.title')}
                  </h2>
                  <p className="text-sm text-muted-foreground -mt-4">
                    {t('useCaseDetail.application.subtitle')}
                  </p>

                  {appLoadError ? (
                    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-0">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-muted-foreground">
                            {appLoadError}
                          </p>
                          {!showChangeApp && (
                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                              <button
                                onClick={() => { setPendingAppId(useCase?.forge_application_id ?? null); setShowChangeApp(true); }}
                                className="text-sm font-medium text-yellow-700 dark:text-yellow-400 underline underline-offset-2 hover:text-yellow-800 dark:hover:text-yellow-300"
                              >
                                {t('useCaseDetail.application.changeApp', 'Changer l\'application associée')}
                              </button>
                              <button
                                onClick={handleUnlinkApp}
                                disabled={isUnlinkingApp}
                                className="text-sm font-medium text-yellow-700 dark:text-yellow-400 underline underline-offset-2 hover:text-yellow-800 dark:hover:text-yellow-300 disabled:opacity-50"
                              >
                                {isUnlinkingApp ? t('global.saving', 'Enregistrement…') : t('useCaseDetail.application.unlinkApp', 'Dissocier l\'application')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      {showChangeApp && (
                        <div className="mt-4 pt-4 border-t border-yellow-400/30 space-y-3">
                          <ApplicationSelector
                            forgeId={domainForgeId}
                            selectedApplicationId={pendingAppId}
                            onSelect={(appId) => setPendingAppId(appId)}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={handleSaveAppChange}
                              disabled={isSavingApp || pendingAppId === useCase?.forge_application_id}
                            >
                              {isSavingApp ? t('global.saving', 'Enregistrement…') : t('global.save', 'Enregistrer')}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setShowChangeApp(false); setPendingAppId(null); }}
                            >
                              {t('global.cancel')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <ApplicationManagementModule
                      useCaseId={id!}
                      useCaseName={useCase?.name || ''}
                      applicationId={useCase?.forge_application_id}
                      domainForgeId={domainForgeId}
                      currentUserEmail={currentUser?.email || 'unknown'}
                      onApplicationCreated={() => refetchUseCase()}
                      onApplicationDeleted={() => refetchUseCase()}
                      onApplicationUnlinked={() => refetchUseCase()}
                      hideUseCaseLink={true}
                      cadrageId={linkedCadrages[0]?.id}
                      cadrageName={linkedCadrages[0]?.name}
                    />
                  )}
                </div>
              </Tab.Panel>
            )}
            <Tab.Panel>
              <DiscussionTab
                comments={discussionHookResult.comments}
                isLoading={discussionHookResult.isLoading}
                error={discussionHookResult.error}
                createComment={discussionHookResult.createComment}
                updateComment={discussionHookResult.updateComment}
                deleteComment={discussionHookResult.deleteComment}
              />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>

        <NewCadrageModal
          isOpen={isNewCadrageModalOpen}
          onClose={() => setIsNewCadrageModalOpen(false)}
          onSuccess={() => {
            setIsNewCadrageModalOpen(false);
            fetchLinkedCadrages();
          }}
          preselectedUseCaseId={id}
          preselectedUseCaseName={useCase?.name}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          open={showDeleteModal}
          title={t('useCaseDetail.deleteTitle', 'Supprimer le cas d\'usage')}
          message={t(
            'useCaseDetail.deleteMessage',
            'Cette action supprimera définitivement le cas d\'usage "{{name}}" et toutes ses solutions associées (qualifications, commentaires, applications Forge liées). Cette action est irréversible.',
            { name: useCase?.name || '' }
          )}
          confirmText={t('useCaseDetail.deleteConfirm', 'Supprimer définitivement')}
          cancelText={t('global.cancel')}
          variant="danger"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          loading={isDeleting}
          requireTextMatch={{
            text: useCase?.name || '',
            label: t('useCaseDetail.deleteInputLabel', 'Tapez le nom du cas d\'usage pour confirmer'),
          }}
          inputValue={useCaseNameInput}
          onInputChange={setUseCaseNameInput}
        />
      </div>
    </PageLayout>
  );
};

export default UseCaseDetailPage;
